/**
 * components/inspection/InspectionFindingForm.js — ШАЛГАЛТЫН ЗӨРЧЛИЙН МАЯГТ.
 *
 * ⚠ ЗОРИЛГО: шалгах хуудсан дээр зөрчил бүртгэхэд гарах маягт нь
 *   «Зөрчил бүртгэх» хуудастай ЯГ ИЖИЛ харагдах ёстой (хэрэглэгчийн
 *   шаардлага, 2026-09-29). Тиймээс шинэ маягт зурахгүй — тэр хуудсыг
 *   угсардаг `HazardFormComposer`-ЫГ ӨӨРИЙГ нь дуудна.
 *
 * ⚠ ЯАГААД ЗУРАГЛАЛ ХЭРЭГТЭЙ ВЭ:
 *   Composer нь зөрчлийн бүртгэлийн (`hazard_reports`) маягтын утгын
 *   бүтцээр ажилладаг бол энд хадгалагдах зүйл нь ШАЛГАЛТЫН ЗӨРЧЛИЙН
 *   МӨР (`inspection_item_result`). Багануудын нэршлийг хоёр талд
 *   ЗОРИУД ижил болгосон тул зураглал нь бараг ижилтгэл — зөвхөн
 *   цөөн талбар нэр зөрнө:
 *       description  ↔ finding_text
 *       worker       ↔ violator_tn / violator_name / violator_position
 *       assigneeTn   ↔ responsible_tn
 *       dueDate      ↔ due_date
 *       startDate    ↔ remedy_start_date
 *       remedy       ↔ remedy_plan
 *       dueNote      ↔ remedy_due_note
 *
 * ⚠ НУУСАН ХЭСГҮҮД: байршил, огноо, үзлэгийн төрөл/шат нь ШАЛГАЛТЫНХ
 *   болохоос зөрчлийн мөрийнх БИШ. Харуулбал хэрэглэгч бөглөөд
 *   хадгалагдахгүй — чимээгүй алдагдах талбар үүснэ.
 *
 * ⚠ ЗУРАГ: composer-ийн `photo` хэсэг нь `form.photos`-д хуримтлуулдаг
 *   бол шалгалтын зураг нь `xab.inspection_file`-д мөрийн id-гаар
 *   хадгалагддаг. Тиймээс энд composer-ийн хэсгийг нуугаад, аль хэдийн
 *   ажиллаж байгаа `FindingPhotos`-ыг доор нь байрлуулна.
 */
import React, { useMemo } from "react";
import { Radio, Select } from "antd";
import moment from "moment";

import HazardFormComposer from "src/components/hazard/HazardFormComposer";
/* ⚠ ЦОНХНЫ БУСАД ХЭСГТЭЙ ИЖИЛ САВЛАГАА (2026-11-01,
   хэрэглэгч: «зөрчил бүртгэх хуудасыг цэгцтэй загварлаг
   болгох, одоо их замбараагүй харагдаж байна»). Composer-ийн бүх
   хэсэг `Card` + `SectionTitle`-тай боловч доорх тэнхлэгийн блок
   нь нүцгэн `div` байсан тул маягт хоёр өөр стильтэй харагдаж байв. */
import { Card, SectionTitle } from "src/components/hazard/common";

/* Шалгалтад хамаарах — зөрчлийн МӨРӨНД хадгалагддаггүй хэсгүүд. */
const HIDDEN = [
  "location",
  "date",
  "inspectionType",
  "inspectionLevel",
  "photo",
  "severity",
  "whoExposed",
  "hazardType",
  // Хадгалалт нь ГАДНА — шалгах хуудас өөрөө автоматаар хадгалдаг.
  // Хоёр өөр хадгалалт байвал хэрэглэгч төөрөгдөнө.
  "saveBar",
];

/* ⚠ «ЗӨРЧИЛ ГАРГАСАН АЖИЛТАН» нь ЗӨВХӨН «Хувь хүн» тэнхлэгт
     (хэрэглэгч 2026-10-30: «Зөрчил гаргасан ажилтан нь хувь хүн гэсэн
     үед харагдана»).

   Composer-ийн `worker` хэсэг = `WorkerLookupSection` бөгөөд тэр нь
   ЗӨВХӨН зөрчил ГАРГАГЧИЙГ (`violator_*`) асуудаг. Арилгах ХАРИУЦАГЧ
   (`assigneeTn` → `responsible_tn`) нь ӨӨР хэсэгт — `remedy`-д —
   байдаг тул үүнийг нуухад хариуцагчийн сонгогч ХӨНДӨГДӨХГҮЙ.
   ⚠ Хоёрыг андуурч `remedy`-г нуувал ЗААВАЛ бөглөх хариуцагч, хугацаа
     хоёр алга болж, зөрчил бүхэлдээ ноорогт гацна. */
const WORKER_SECTION = "worker";

/** Сервер «1,2,3» мөрөөр буцаадаг → массив. Хоосон бол ганц утгаас. */
function splitIds(raw, single) {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string" && raw.trim())
    return raw.split(",").map((x) => Number(x)).filter(Boolean);
  return single ? [single] : [];
}

/** Сервер «a | b» мөрөөр буцаадаг → массив. */
function splitTexts(raw, single) {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string" && raw.trim()) return raw.split(" | ");
  return single ? [single] : [];
}

/** Шалгалтын зөрчлийн мөр → маягтын утга. */
export function rowToForm(row = {}) {
  return {
    worker: row.violator_tn
      ? {
          tn: row.violator_tn,
          name: row.violator_name || "",
          position: row.violator_position || "",
        }
      : null,
    description: row.finding_text || "",
    photos: [],
    gps: { lat: null, lng: null, accuracy: null },
    violation_type_id: row.violation_type_id ?? null,
    violation_type_text: row.violation_type_text ?? null,
    violation_clause_id: row.violation_clause_id ?? null,
    violation_clause_text: row.violation_clause_text ?? null,
    /* ОЛОН СОНГОЛТ — «1,2,3» мөрийг массив болгоно.
       ⚠ ХООСОН МАССИВ ХАТУУ ӨГЧ БОЛОХГҮЙ: ViolationClassSection нь
         `_ids` массив байвал түүгээр зогсдог тул хадгалагдсан сонголт
         харагдахаа больж, «сонгогдохгүй байна» гэж мэдрэгдэнэ
         (2026-09-29-ний алдаа). Ганц утгаас ч сэргээнэ. */
    violation_type_ids: splitIds(row.violation_type_ids, row.violation_type_id),
    violation_type_texts: splitTexts(
      row.violation_type_texts,
      row.violation_type_text,
    ),
    violation_clause_ids: splitIds(
      row.violation_clause_ids,
      row.violation_clause_id,
    ),
    violation_clause_texts: splitTexts(
      row.violation_clause_texts,
      row.violation_clause_text,
    ),
    penalty_id: row.penalty_id ?? null,
    penalty_text: row.penalty_text ?? null,
    measure_id: row.measure_id ?? null,
    measure_text: row.measure_text ?? null,
    info_class_id: row.info_class_id ?? null,
    info_class_text: row.info_class_text ?? null,
    /* ⚠ ХАТУУ `null` БИЧИЖ БОЛОХГҮЙ (2026-09-30-ны гэмтлийн сургамж).
       «Хөдөлмөрийн нөхцөл» товч нь `value.haz_level_id === r.id` гэж,
       «Эрсдэлтэй эсэх» Radio нь `value.is_risk`-аар «сонгогдсон» эсэхээ
       шийддэг. Хатуу `null` өгвөл дарах бүрд БУЦААД ЦУЦЛАГДАЖ,
       хэрэглэгчийн хувьд «дарж байна, сонгогдохгүй» болно. */
    haz_level_id: row.haz_level_id ?? null,
    haz_level_text: row.haz_level_text ?? null,
    /* ГУРВАН төлөв: null тэмдэглээгүй · true тийм · false үгүй.
       ⚠ `?? null` ЗААВАЛ — `|| null` бол `false` нь null болж хувирч,
         «Үгүй» гэсэн сонголт «тэмдэглээгүй» болно. */
    is_risk: row.is_risk ?? null,
    // Эрсдэлийн түвшин (1 Бага · 2 Дунд · 3 Өндөр)
    severity: row.severity ?? null,
    remedy: row.remedy_plan || "",
    dueNote: row.remedy_due_note || "",
    responsibleBnCode: row.responsible_bn_code || "",
    responsibleBnName: row.responsible_bn_name || "",
    assigneeTn: row.responsible_tn || null,
    assigneeName: row.responsible_name || "",
    startDate: row.remedy_start_date ? moment(row.remedy_start_date) : null,
    dueDate: row.due_date ? moment(row.due_date) : null,
    /* Гүйцэтгэх хугацааны ТӨРӨЛ (2026-11-01):
       standing | urgent | urgent_standing | dated.
       ⚠ АНХДАГЧ ТАВИХГҮЙ: хуучин 2 300 зөрчил дээр байхгүй
         бөгөөд төрөл дәәрсэн үед сервер `due_date`-ийг дарж
         бичиж магад — хэрэглэгч СОНГОХ хүртэл `undefined`. */
    dueKind: row.due_kind || undefined,
    /* Хариуцагчийн ТӨРӨЛ (2026-11-01): person | first_line | ita.
       ⚠ Хоосон/танигдаагүй нь «тодорхой хүн»-тэй ижил — ТН заавал. */
    responsibleKind: row.responsible_kind || undefined,
    /* ЧЕКЛЭСЭН ХҮМҮҮС.
       ⚠⚠ ЭХЛЭЭД МӨРӨН ДЭЭРХ утга, ДАРАА НЬ даалгавруудаас (2026-11-03).
         Урьд нь ЗӨВХӨН `assignments`-аас уншдаг байсан тул чек
         ХИЙГДЭХГҮЙ байв: хэрэглэгч чеклэхэд `formToRow` нь
         `row.responsible_tns`-д бичдэг ч дараагийн рендерт энэ
         функц түүнийг ҮЛ ТООМСОРЛОЖ даалгавруудаас (хоосон) дахин
         бодож, сонголт ТЭР ДАРУЙ арчигддаг байсан (хэрэглэгч:
         «чек хийгдэхгүй байна»).
       ⚠ Мөрөнд `responsible_tns` гэсэн БААЗЫН багана БАЙХГҮЙ — энэ нь
         зөвхөн маягтын ТҮР утга. Хадгалсны дараа жинхэнэ эх сурвалж
         нь `assignments` (хүн тус бүр ӨӨРИЙН даалгавартай). */
    responsibleTns: Array.isArray(row.responsible_tns)
      ? row.responsible_tns.map(String)
      : (row.assignments || [])
          .filter((a) => a && a.responsible_tn)
          .map((a) => String(a.responsible_tn)),
  };
}

/** Маягтын утга → мөрийн patch (ЗӨВХӨН мөрөнд байдаг талбарууд). */
export function formToRow(form = {}) {
  return {
    finding_text: form.description || null,
    violator_tn: form.worker ? form.worker.tn : null,
    violator_name: form.worker ? form.worker.name : null,
    violator_position: form.worker ? form.worker.position : null,
    violation_type_id: form.violation_type_id ?? null,
    violation_type_text: form.violation_type_text ?? null,
    violation_clause_id: form.violation_clause_id ?? null,
    violation_clause_text: form.violation_clause_text ?? null,
    penalty_id: form.penalty_id ?? null,
    penalty_text: form.penalty_text ?? null,
    measure_id: form.measure_id ?? null,
    measure_text: form.measure_text ?? null,
    info_class_id: form.info_class_id ?? null,
    info_class_text: form.info_class_text ?? null,
    haz_level_id: form.haz_level_id ?? null,
    haz_level_text: form.haz_level_text ?? null,
    // ⚠ `?? null` ЗААВАЛ (дээрх тайлбарыг үз)
    is_risk: form.is_risk ?? null,
    severity: form.severity ?? null,
    remedy_plan: form.remedy || null,
    remedy_due_note: form.dueNote || null,
    responsible_bn_code: form.responsibleBnCode || null,
    responsible_bn_name: form.responsibleBnName || null,
    responsible_tn: form.assigneeTn || null,
    responsible_name: form.assigneeName || null,
    remedy_start_date: form.startDate
      ? moment(form.startDate).format("YYYY-MM-DD HH:mm:ss")
      : null,
    /* ⚠ `violated_rule` нь ХУУЧИН чөлөөт талбар боловч албан шаардлага,
       удирдамжийн seed зэрэгт ашиглагдсаар байгаа. Маягтад тусдаа
       оролт байхгүй тул зөрчсөн ЗААЛТЫН текстээр нөхнө — эс бөгөөс
       хоосон үлдэж, тэр урсгалууд эвдэрнэ. */
    violated_rule: form.violation_clause_text || null,
    /* ОЛОН СОНГОЛТЫГ БҮТНЭЭР хадгална — эс бөгөөс 2, 3 дахь сонголт
       чимээгүй алга болно (маягт 3 хүртэл зөвшөөрдөг). */
    violation_type_ids: (form.violation_type_ids || []).join(",") || null,
    violation_type_texts:
      (form.violation_type_texts || []).join(" | ") || null,
    violation_clause_ids: (form.violation_clause_ids || []).join(",") || null,
    violation_clause_texts:
      (form.violation_clause_texts || []).join(" | ") || null,
    due_kind: form.dueKind || null,
    responsible_kind: form.responsibleKind || null,
    /* ⚠ БААЗЫН БАГАНА БИШ — `buildPayload`-ийн цагаан жагсаалтад
       ОРООГҮЙ тул PUT-д явахгүй. `InspectionChecklistForm` нь
       хадгалсны ДАРАА үүгээр даалгавруудыг синк хийнэ. */
    responsible_tns: Array.isArray(form.responsibleTns)
      ? form.responsibleTns.map(String)
      : [],
    due_date: form.dueDate
      ? moment(form.dueDate).format("YYYY-MM-DD HH:mm:ss")
      : null,
  };
}

/**
 * Props:
 *   row        — шалгалтын зөрчлийн мөр
 *   mode       — composer-ийн горим (хэрэглэгчийн эрхээс, ViolationQuickModal-тай ижил)
 *   onPatch    — (patch) => void, мөрийг шинэчилнэ
 *   disabled   — хаагдсан шалгалт
 *   categories · severities · onPickFile · uploading — composer-т дамжина
 *   extraBottom — доор нэмэх агуулга (шалгалтын төлөв г.м.)
 *   descriptionSide — «Илэрсэн зөрчлийн дэлгэрэнгүй»-ийн ХАЖУУД суух
 *     агуулга (зөрчлийн зураг). Доор биш хажууд нь байрлуулснаар цонх
 *     богиносч, зураг тайлбартайгаа зэрэг харагдана (2026-10-01).
 */
export default function InspectionFindingForm({
  row = {},
  mode = "violation",
  onPatch,
  disabled = false,
  categories = [],
  severities = [],
  /** ХЭМАБМТ-06 Хавсралт №1 — ноцтой зөрчлийн ХААЛТТАЙ жагсаалт */
  seriousList = [],
  onPickFile,
  uploading = false,
  extraBottom = null,
  descriptionSide = null,
  /** Тухайн цех/хэсгийн АЖЛЫН БАЙРНУУД — xab.bn_workplace-аас.
      Хоосон бол сонгогч нь «ажлын байр бүртгэгдээгүй» гэж харагдана. */
  workplaces = [],
  /** Тухайн ЦЕХИЙН ТОНОГ ТӨХӨӨРӨМЖ — xab.hr_device-аас (2026-10-30).
      ⚠ Лавлах нь зөвхөн Баяжуулах үйлдвэрт бүрэн (595 ТТ-ийн 589) тул
        ихэнх цехэд ХООСОН байна — тэр нь алдаа БИШ. */
  devices = [],
  /** Цонхны ДЭЭД анхааруулга (дутуу талбарын мэдэгдэл).
      ⚠ Тэнхлэгийн картын ХАЖУУД, 2 баганаар суух ёстой (хэрэглэгч
        2026-11-01) тул цонхны биед биш ЭНД дамжуулагдана. */
  topAlert = null,
}) {
  const form = useMemo(() => rowToForm(row), [row]);
  /* Хавсралт №1-ийн сонгогдсон заалтууд. Хуучин мөрд ганц `_id` л
     байсан тул түүнээс ч сэргээнэ. */
  const seriousIds = useMemo(
    () => splitIds(row.serious_violation_ids, row.serious_violation_id),
    [row.serious_violation_ids, row.serious_violation_id],
  );

  const handleChange = (next) => {
    if (disabled) return;
    onPatch?.(formToRow(next));
  };

  /* ── ЗӨРЧЛИЙГ ОНИЛОХ 3 ТЭНХЛЭГ (2026-10-30) ───────────────────────
     Хэрэглэгч: «Зөрчил бүртгэх хуудсанд 3 сонголт оруулах 1. ажлын
     байр, 2. Тоног төхөөрөмж, 3. Хувь хүн».

     ⚠ ТЭНХЛЭГ нь БААЗАД ХАДГАЛАГДДАГГҮЙ — шинэ багана нэмэх
       шаардлагагүй. Аль талбар бөглөгдсөнөөс нь ӨӨРӨӨ гарна:
         workplace_id → ажлын байр · device_id → ТТ · violator_tn → хүн
       Ингэснээр хуучин 2 300 зөрчил ч зөв тэнхлэгтэй харагдана.

     ⚠ ХООРОНДОО ҮЛ ТООЦОЛДОХГҮЙ: зөрчил хүн дээр ч, ажлын байр дээр ч,
       хоёул дээр ч гарч болно (2026-10-23-ны шийдвэр). Тиймээс тэнхлэг
       солиход НӨГӨӨ талбарыг ЦЭВЭРЛЭХГҮЙ — зөвхөн ХАРАГДАХ хэсгийг
       сольж, бөглөгдсөн бусад тэнхлэгийг ДООР тэмдэгээр үзүүлнэ. */
  const axisOf = (r) => {
    if (r.device_id) return "device";
    if (r.violator_tn) return "person";
    return "workplace";
  };
  const [axis, setAxis] = React.useState(() => axisOf(row));
  /* Нуух хэсгүүд — тэнхлэгээс хамаарна (дээрх WORKER_SECTION-ийн
     тайлбарыг үз). */
  const hidden = useMemo(
    () => (axis === "person" ? HIDDEN : [...HIDDEN, WORKER_SECTION]),
    [axis],
  );

  return (
    <div>
      {/* ── ЗӨРЧЛИЙН ХҮНДРЭЛ ──────────────────────────────────────────
          ⚠ Хэрэглэгчийн шаардлага: «Зөрчлийн ангилал дээр харин ноцтой /
            ноцтой бус». Composer-ийн `violationClass` хэсэг нь лавлахын
            төрлүүдийг асуудаг бол ЭНЭ нь ХЭМАБМТ-06-ийн хүндрэлийн зэрэг —
            хоёр өөр зүйл тул тусдаа, түүний ДЭЭР байрлуулав. */}

      {/* ── АЖЛЫН БАЙРАН ДЭЭРХ ЗӨРЧИЛ (2026-10-23) ──────────────────
          Хэрэглэгч: «зөрчлийг хүн дээр гаргах мөн ажлын байран дээр
          зөрчил бүртгэх тохиолдол гардаг» · «нэг шалгалтаар явж байгаад
          тухайн ажлын байранд хэд хэдэн зөрчил бүртгэх тохиолдол гарна».

          ⚠ ХҮНТЭЙ ХАМТ байж болно — зөрчил хүн дээр ч, ажлын байр дээр ч,
            хоёул дээр ч гарч болно. Тиймээс «аль нэгийг» гэж албадахгүй.

          ⚠ Composer-ийн ДОТОР биш ГАДНА: composer нь зөрчлийн бүртгэлийн
            (`hazard_reports`) маягт бөгөөд түүнд ажлын байр ХАДГАЛАГДДАГГҮЙ
            (`hazard_reports.workplace_id` нь хоёр клиентээс ч явдаггүй).
            Дотор нь тавибал мобайл/вэбийн зөрчил бүртгэх хуудсанд ч гарч,
            бөглөсөн утга чимээгүй алдагдана — `FindingPhotos`-ийг гадна
            байрлуулсантай ижил шалтгаан.

          ⚠ `onPatch`-аар ШУУД дамжуулна, `formToRow`-д оруулахгүй:
            composer-ийн өөрчлөлт бүрд `formToRow` бүтэн patch буцаадаг
            тул тэнд байхгүй талбар хөндөгдөхгүй (`violation_kind` ч
            яг ийм замаар ажилладаг). */}
      {/* ── ТЭНХЛЭГ | АНХААРУУЛГА — 2 БАГАНА (2026-11-01) ───────────
          Хэрэглэгч: «Зөрчил юун дээр гарсан энэ хэсэг болон энэ
          мэдээллийн хэсэг 2-г 2 багана хэлбэрээр харуул».
          ⚠ 900px-ээс доош НЭГ багана: гар утсанд хоёр багана нь
            160px болж сонгогч ашиглах аргагүй болно (composer-ийн
            `hfc-grid`-тай ИЖИЛ хил).
          ⚠ Анхааруулга БАЙХГҮЙ үед тэнхлэг нь БҮТЭН өргөнөө авна —
            хоосон багана үлдвэл «нэг зүйл дутуу» мэт харагдана. */}
      <style>{`
        .ifv-top { display: grid; grid-template-columns: 1fr 1fr;
                   gap: 12px; align-items: start; margin-bottom: 12px; }
        .ifv-top.ifv-top-one { grid-template-columns: 1fr; }
        .ifv-top > div { min-width: 0; }
        .ifv-top .hz-card { margin-bottom: 0 !important; }
        @media (max-width: 900px) {
          .ifv-top { grid-template-columns: 1fr; gap: 10px; }
        }
      `}</style>
      {/* ⚠ БАРУУН БАГАНА нь мэдэгдэл БА/ЭСВЭЛ зурагтай үед гарна
          (2026-11-03, хэрэглэгч: «зураг оруулах хэсгийг Зөрчил юун
          дээр гарсан хэсгийн баруун талд мэдэгдлийн дор харуулах»).
          Өмнө нь зөвхөн `topAlert`-оор шийддэг байсан тул мэдэгдэлгүй
          үед баруун багана алга болж, зураг унах байсан. */}
      <div
        className={
          "ifv-top" + (topAlert || descriptionSide ? "" : " ifv-top-one")
        }
      >
      <Card>
        <SectionTitle>Зөрчил юун дээр гарсан</SectionTitle>
        {/* ⚠ ТОВЧНЫ МӨР нь ӨӨРИЙН БЛОКТ: `Radio.Group` нь inline-block
            тул доорх сонгогчтой нэг мөрөнд шахагдах боломжтой. */}
        <div style={{ display: "block", marginBottom: 10 }}>
          <Radio.Group
            size="small"
            optionType="button"
            buttonStyle="solid"
            disabled={disabled}
            value={axis}
            onChange={(e) => setAxis(e.target.value)}
            options={[
              { label: "Ажлын байр", value: "workplace" },
              { label: "Тоног төхөөрөмж", value: "device" },
              { label: "Хувь хүн", value: "person" },
            ]}
          />
        </div>

        {/* ⚠⚠ СОНГОСОН ТЭНХЛЭГИЙНХ НЬ Л ХАРАГДАНА (хэрэглэгчийн
            ТОДОРХОЙЛОЛТ 2026-10-30: «Зөрчил юун дээр гарсан-аас
            шалтгаална — ажлын байр бол зөвхөн ажлын байр, ТТ бол ТТ-ийн
            жагсаалт, хувь хүн бол … гэх мэт»).

            Түүхэн тэмдэглэл (хожим «яагаад ингэв» гэж асуухад):
              · эхлээд радиогоор СОЛЬДОГ байв,
              · дараа «ажлын байр болон ТТ 2-ыг нэг мөрөнд» гэснээр
                хоёуланг зэрэг харуулав,
              · одоо дахин СОЛЬДОГ болов — хэрэглэгчийн ХАМГИЙН
                СҮҮЛИЙН шийдвэр. Шийдвэр буцсан тул өмнөхөөр нь
                «сайжруулж» болохгүй.

            ⚠ ДАТА ЦЭВЭРЛЭГДЭХГҮЙ: тэнхлэг солиход `workplace_id`,
              `device_id`, `violator_*`-ийн аль нь ч хөндөгдөхгүй.
              Хэрэв аль нэг нь аль хэдийн бөглөгдсөн бол доорх
              «Бүртгэгдсэн: …» мөрөнд харагдана — ингэснээр нуугдсан
              талбарт үлдсэн утга хэрэглэгчийн мэдэлгүй хадгалагдахгүй.
              (Цэвэрлэх нь ЗӨВ санагдаж болох ч зөрчил байранд ч,
              төхөөрөмж дээр ч зэрэг хамаарах тохиолдол бодитой —
              2026-10-23-ны шийдвэр. Хэрэв «нэгийг сонгоход бусдыг
              цэвэрлэ» гэвэл ЗОРИУД нэмнэ.) */}

        {/* ① АЖЛЫН БАЙР
            ⚠ ЗААВАЛ БЛОК `div`-Д (2026-10-30): `Radio.Group` ба `Select`
              ХОЁУЛАА inline-block тул нүцгэн тавибал товчны мөрөнд
              шахагдаж, сонгогч нь товчны хэсэг мэт харагдана
              (хэрэглэгч: «ажлын байр ТТ 2 залгаатай харагдаж байна»).
              Блок + шошго нь түүнийг ТУСДАА талбар болгоно. */}
        {axis === "workplace" ? (
          <div>
            <div
              style={{ fontSize: 11.5, color: "#8A94A6", marginBottom: 3 }}
            >
              Зөрчил гарсан ажлын байр
            </div>
            <Select
              showSearch
              allowClear
              size="small"
              style={{ width: "100%", maxWidth: 560 }}
              disabled={disabled || workplaces.length === 0}
              placeholder={
                workplaces.length === 0
                  ? "Ажлын байр бүртгэгдээгүй"
                  : "Ажлын байр сонгох"
              }
              optionFilterProp="children"
              value={row.workplace_id || undefined}
              onChange={(v) => onPatch?.({ workplace_id: v ?? null })}
            >
              {workplaces.map((w) => (
                <Select.Option key={w.id} value={w.id}>
                  {[w.code, w.name].filter(Boolean).join(" · ")}
                </Select.Option>
              ))}
            </Select>
          </div>
        ) : null}

        {/* ② ТОНОГ ТӨХӨӨРӨМЖ — xab.hr_device.
            ⚠ ЛАВЛАХ ХООСОН БАЙХ НЬ ХЭВИЙН: 595 ТТ-ийн 589 нь Баяжуулах
              үйлдвэрт, бусад 55 цехэд ТТ бүртгэгдээгүй. Хоосон сонгогч
              нь «эвдэрсэн» гэсэн ойлголт төрүүлдэг тул ШАЛТГААНЫГ доор
              бичнэ. */}
        {axis === "device" ? (
          <div>
            <div
              style={{ fontSize: 11.5, color: "#8A94A6", marginBottom: 3 }}
            >
              Зөрчил гарсан тоног төхөөрөмж
            </div>
            <Select
              showSearch
              allowClear
              size="small"
              style={{ width: "100%", maxWidth: 560 }}
              disabled={disabled || devices.length === 0}
              placeholder={
                devices.length === 0
                  ? "Энэ цехэд тоног төхөөрөмж бүртгэгдээгүй"
                  : "Тоног төхөөрөмж сонгох"
              }
              optionFilterProp="children"
              value={row.device_id || undefined}
              onChange={(v) => onPatch?.({ device_id: v ?? null })}
            >
              {devices.map((d) => (
                <Select.Option key={d.id} value={d.id}>
                  {d.title || d.full_name}
                </Select.Option>
              ))}
            </Select>
            {devices.length === 0 ? (
              <div style={{ fontSize: 11.5, color: "#8A94A6", marginTop: 4 }}>
                Тоног төхөөрөмжийн лавлах нь Баяжуулах үйлдвэрт бүрэн
                бөглөгдсөн. Бусад цехийн ТТ-г «Өндөр эрсдэлт ажил →
                лавлах» дээр бүртгэсний дараа энд сонгогдоно.
              </div>
            ) : null}
          </div>
        ) : null}

        {/* ③ ХУВЬ ХҮН — зөрчил ГАРГАСАН ажилтан (`violator_*`).
            ⚠ `responsible_tn` (зөрчлийг АРИЛГАХ эзэн)-ЭЭС ӨӨР талбар.
              Хоёрыг хольвол хариуцлага буруу хүнд тооцогдоно.
            ⚠ ДАВХАР СОНГОГЧ: доорх «Зөрчил гаргасан ажилтан» хэсэг ч
              ижил талбарыг заадаг. Хоёулаа `row.violator_*`-аас уншдаг
              тул ҮРГЭЛЖ ижил утга харуулна — нэг л эх сурвалж. */}
        {axis === "person" ? (
          /* ⚠ ЭНД СОНГОГЧ ТАВИХГҮЙ. Зөрчил гаргагчийг доорх
             «Зөрчил гаргасан ажилтан» хэсэг (Б/Д-ээр хайж зураг,
             албан тушаал, цех, утсыг нь баталгаажуулдаг) асууна —
             энэ тэнхлэгийг сонгоход тэр хэсэг ГАРНА.
             Хоёр сонгогч тавивал аль нь үнэн болох нь мэдэгдэхгүй,
             мөн буруу хүнд зөрчил бичигдэх эрсдэл үүснэ. */
          <div style={{ fontSize: 11.5, color: "#5B6472" }}>
            <i
              className="fas fa-arrow-down"
              style={{ marginRight: 5, color: "#8A94A6" }}
            />
            Доорх <b>«Зөрчил гаргасан ажилтан»</b> хэсэгт Б/Д-ээр хайж
            сонгоно. ⚠ Энэ нь зөрчлийг ГАРГАСАН хүн — АРИЛГАХ хариуцагчийг
            «Арга хэмжээ» хэсэгт заана, хоёр нь өөр хүн байдаг.
          </div>
        ) : null}

        {/* БУСАД ТЭНХЛЭГТ бөглөгдсөн утга — тэнхлэг солиход юу ч
            алдагдаагүйг хэрэглэгч ХАРАХ ёстой. */}
        {(() => {
          const other = [];
          if (axis !== "workplace" && row.workplace_name)
            other.push(["fa-industry", row.workplace_name]);
          if (axis !== "device" && row.device_name)
            other.push(["fa-cogs", row.device_name]);
          if (axis !== "person" && row.violator_name)
            other.push(["fa-user", row.violator_name]);
          if (!other.length) return null;
          return (
            <div style={{ marginTop: 6, fontSize: 11.5, color: "#5B6472" }}>
              Бүртгэгдсэн:{" "}
              {other.map(([ic, tx]) => (
                <span key={ic} style={{ marginRight: 10 }}>
                  <i
                    className={`fas ${ic}`}
                    style={{ marginRight: 4, color: "#8A94A6" }}
                  />
                  {tx}
                </span>
              ))}
            </div>
          );
        })()}
      </Card>
        {topAlert || descriptionSide ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {topAlert ? <div>{topAlert}</div> : null}
            {/* ЗУРАГ — мэдэгдлийн ДООР (хэрэглэгчийн заасан дараалал) */}
            {descriptionSide || null}
          </div>
        ) : null}
      </div>

      <HazardFormComposer
        /* ⚠ ХОЁР БАГАНА: модал дотор маягт дэлгэц дүүрэн болж, хадгалах
             товч хүртэл гүйлгэх шаардлагатай байв. Гар утсанд (≤900px)
             автоматаар нэг багана болно. */
        twoColumn
        /* Зөрчлийн зураг — тайлбарын баруун талд. ⚠ Composer-ийн `photo`
           хэсэг нь HIDDEN-д хэвээр: тэр нь ӨӨР хадгалалттай (доорх
           файлын толгойн тайлбарыг үз). */
        /* ⚠ ЗУРАГ нь ангиллын мөрнөөс «Зөрчил юун дээр гарсан»-ы
           БАРУУН тал руу шилжив (2026-11-03, хэрэглэгч). Гэвч
           ангиллын мөр нь БҮТЭН ӨРГӨН хэвээр байх ёстой: тэнд
           «Зөрчлийн ангилал» ба «Шийтгэлийн төрөл» гэсэн ХОЁР групп
           байгаа тул хагас нүдэнд суувал групп бүр дөрөвний нэг
           өргөнтэй шахагдана (2026-11-02-нд мэдээлсэн гомдол). */
        violationClassWide
        mode={mode}
        hideSections={hidden}
        /* ⚠ ХҮНДРЭЛ нь «Зөрчлийн ангилал» картын ДОТОР орно. Тусдаа карт
             болговол ижил гарчигтай хоёр карт зэрэгцэж, хэрэглэгч
             «дахин сонгох юм уу» гэж эргэлздэг (2026-09-29). */
        /* ⚠ НОЦТОЙ бол «Зөрчлийн төрөл» (ноцтой БУСЫН лавлах) НУУГДАНА:
             ноцтой зөрчил нь Хавсралт №1-ийн ХААЛТТАЙ жагсаалтаас
             сонгогддог. Хоёул зэрэг харагдвал ангилал давхарлаж, аль нь
             үнэн болох нь мэдэгдэхгүй болно. */
        violationClassHideKinds={
          row.violation_kind === "serious" ? ["type"] : []
        }
        violationClassExtra={
          <div>
      {/* ⚠ ЗААВАЛ БӨГЛӨХ — улаан од (2026-11-05, хэрэглэгч). `violation_kind`
          нь серверийн `FINDING_REQUIRED`-ийн нэг: дутуу бол зөрчил
          «Ноорог (дутуу)» төлөвт орж, шалгалт хаагдахгүй. */}
      <div style={{ fontSize: 12, color: "#8A94A6", marginBottom: 4 }}>
        Хүндрэл
        <b
          title="Заавал бөглөнө — дутуу бол зөрчил «Ноорог (дутуу)» төлөвт орж, шалгалт хаагдахгүй"
          style={{ color: "#DC2626", marginLeft: 4, fontSize: 13 }}
        >
          *
        </b>
      </div>
      <Radio.Group
        disabled={disabled}
        value={row.violation_kind || undefined}
        onChange={(e) => onPatch?.({ violation_kind: e.target.value })}
        optionType="button"
        buttonStyle="solid"
        size="small"
        options={[
          { label: "Ноцтой", value: "serious" },
          { label: "Ноцтой бус", value: "minor" },
        ]}
      />
      {/* ⚠ НОЦТОЙ бол Хавсралт №1-ийн жагсаалтаас ЗААВАЛ сонгоно.
          Энэ нь албан шаардлагын эрх зүйн үндэслэл болдог тул
          чөлөөт бичвэрээр орлуулж БОЛОХГҮЙ. */}
      {row.violation_kind === "serious" ? (
        <div style={{ marginTop: 8 }}>
          {/* ⚠ ОЛОН СОНГОЛТТОЙ: нэг зөрчил Хавсралт №1-ийн хэд хэдэн
              заалтыг зэрэг зөрчиж болно (2026-09-29, хэрэглэгч).
              Үндсэн (эхний) нь `serious_violation_id`-д үлдэнэ — «хамгийн
              олон давтагдсан ноцтой зөрчил» задаргаа, албан шаардлагын
              эшлэл түүгээр явдаг. */}
          <Select
            size="small"
            mode="multiple"
            maxTagCount="responsive"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: "100%" }}
            dropdownMatchSelectWidth={false}
            dropdownStyle={{ minWidth: 420, maxWidth: "min(760px, 92vw)" }}
            disabled={disabled}
            placeholder="Ноцтой зөрчил (ХЭМАБМТ-06 Хавсралт №1)"
            value={seriousIds}
            onChange={(ids) => {
              const list = ids || [];
              onPatch?.({
                serious_violation_ids: list.join(",") || null,
                // ҮНДСЭН = эхнийх (хуучин тайлангууд үүгээр явдаг)
                serious_violation_id: list[0] ?? null,
              });
            }}
            options={(seriousList || []).map((sv) => ({
              value: sv.id,
              label: (sv.code || String(sv.no)) + ". " + sv.text,
            }))}
          />
          {seriousIds.map((sid) => {
            const sv = (seriousList || []).find((x) => x.id === sid);
            return sv ? (
              <div
                // ⚠ `key` ЗААВАЛ — массивын элемент. Байхгүй бол React
                //   «Each child in a list should have a unique key» гэж
                //   анхааруулахаас гадна сонголт солиход буруу мөрийг
                //   дахь ашиглаж хүндрэл зөрөөд харагдаж мэднэ.
                key={sid}
                style={{
                  marginTop: 4,
                  padding: "4px 8px",
                  borderRadius: 6,
                  background: "#FEE2E2",
                  color: "#7F1D1D",
                  fontSize: 11.5,
                  lineHeight: 1.4,
                }}
              >
                <b>№{sv.no}</b>
                {sv.code ? (
                  <code style={{ marginLeft: 4, fontSize: 10.5 }}>
                    {sv.code}
                  </code>
                ) : null}
                {" — "}
                {sv.text}
              </div>
            ) : null;
          })}
        </div>
      ) : null}
          </div>
        }
        remedyExtras
        remedyDueKind
        pairInfoWithDescription
        hideDeptSelectorsOverride
        value={form}
        onChange={handleChange}
        categories={categories}
        severities={severities}
        onPickFile={onPickFile}
        uploading={uploading}
        /* Хадгалалт нь ГАДНА — шалгах хуудас өөрөө автоматаар хадгалдаг
           тул энэ маягтад тусдаа «Хадгалах» товч ХЭРЭГГҮЙ. Байвал
           хэрэглэгч хоёр өөр хадгалалттай тулгарч төөрөгдөнө. */
        onSave={null}
        onCancel={null}
      />


      {extraBottom}
    </div>
  );
}
